import { useMemo } from 'react';

import { IS_SERVER } from '@/renderer/utils/ssr';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import { BreakdownTabs } from './BreakdownTabs';
import type { ResultsFilters } from './FilterTypes';
import { SPORT_IDS, resolveSportIds } from './consts';

type ChartResponse = API.MembershipProductStats & {
  chart: { date: string; units: string | number }[];
} & {
  playType?: Record<API.Results, API.MembershipProductStats>;
} & {
  period?: Record<API.Results, API.MembershipProductStats>;
};

const playTypeFilter = (
  playType?: API.PlayTypes | API.PlayTypes[] | 'full' | 'st' | 'hp' | 'pf',
) =>
  playType === 'full'
    ? ['side', 'total', 'prop', 'future', 'not-available']
    : playType === 'hp'
      ? ['side', 'total', 'not-available']
      : playType === 'st'
        ? ['side', 'total']
        : playType === 'pf'
          ? ['prop', 'future']
          : playType && typeof playType === 'string'
            ? [playType]
            : playType || ['side', 'total', 'prop', 'future', 'not-available'];

export function ResultsBreakdowns({
  baseUrl,
  userId,
  filters,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase, startDate, endDate, playType, free, gameType } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);
  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  const getChartRequest = useMemo<RequestSchema<ChartResponse>>(
    () => ({ name: 'getChartRequest', input: `/api/v1/messageReceipt/stats/${userId}` }),
    [userId],
  );
  const chartRequest = useRequest(
    getChartRequest,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        clientOnly: !isArchive, // /results (my results) can't fetch this in SSR (needs cookies)
        query: {
          chart: true,
          count: true,
          tz:
            (typeof Intl !== 'undefined' &&
              Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone) ||
            undefined,
          playType: playTypeFilter(playType),
          [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
          'message.serviceIds': serviceIds,
          startDate,
          endDate,
          free,
          gameType,
          groupBy: isArchive ? 'playType' : undefined,
        },
      }),
      [isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  const getStatsRequest = useMemo<RequestSchema<API.ReleaseStats>>(
    () =>
      isArchive
        ? { name: 'getStatsRequest', input: '/api/v1/release/stats' }
        : { name: `getStatsRequest/${userId}`, input: `/api/v1/release/stats/${userId}` },
    [isArchive, userId],
  );

  const statsRequest = useRequest(
    getStatsRequest,
    useMemo(
      () => ({
        // careful not to cache a single person's stats to the server
        cache: !IS_SERVER || userId !== 'me' ? { maxAge: 90 * 1000 } : undefined,
        clientOnly: !isArchive,
        query: {
          data: {
            playType: playTypeFilter(playType),
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            'gameTime$>=': startDate,
            'gameTime$<=': endDate,
            free,
            gameType,
          },
        },
      }),
      [userId, isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  return (
    <ResultsBreakdownsContent
      baseUrl={baseUrl}
      userId={userId}
      filters={filters}
      chartData={chartRequest?.data}
      statsData={statsRequest?.data}
      isLoading={isArchive ? statsRequest.loading : chartRequest.loading}
    />
  );
}

export function ResultsBreakdownsContent({
  baseUrl,
  userId,
  filters,
  statsData,
  chartData,
  isLoading: isDataLoading,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  statsData?: API.ReleaseStats;
  chartData?: ChartResponse;
  isLoading?: boolean;
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase } = filters;

  const stats = statsData;

  const isPending = isArchive ? statsData === undefined : chartData === undefined;
  const isLoading = isDataLoading || isPending;

  // The single numeric sportId when exactly one sport is in view, else null. Drives both
  // the single-sport breakdown tabs and the CFB-only Division gate.
  const singleSportId = useMemo(() => {
    const resolved = resolveSportIds(sportIdBase);
    return typeof resolved === 'number' && Object.values(SPORT_IDS).includes(resolved)
      ? resolved
      : null;
  }, [sportIdBase]);
  const isSingleSport = singleSportId !== null;

  // The record/units headline + chart now live in the header card (ResultsHeader); this
  // component renders only the tabbed breakdown card below it (#2004).
  return (
    <BreakdownTabs
      stats={stats}
      baseUrl={baseUrl}
      isLoading={isLoading}
      isSingleSport={isSingleSport}
      sportId={singleSportId}
    />
  );
}
